/* eslint-disable react/no-danger */
import { Button as AlertButton, InlineAlert } from '@eventbrite/design-ops-ds';
import { Button } from '@eventbrite/eds-button';
import {
    AlertChunky,
    FlagChunky,
    InfoChunky,
    Religion,
    Stopwatch,
    Trending,
} from '@eventbrite/eds-iconography';
import { Link } from '@eventbrite/eds-link';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { setWindowLocation } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { makeRequest, TD_API_URL } from '@eventbrite/marketing-hub-settings';
import { BenefitsItem } from '@eventbrite/marketing-tools-common';
import { logEvent } from '@eventbrite/statsig';
import React from 'react';
import '../ActivationModal/ActivationModal.scss';
import { trackEvent } from '../analytics/analytics';
import {
    ACTIVATION_MODAL_CATEGORY,
    ACTIVATION_MODAL_CLICKED_CLOSE,
    ACTIVATION_MODAL_CLICKED_GET_STARTED,
    ACTIVATION_MODAL_CLICKED_LEARN_MORE,
    ACTIVATION_MODAL_CLICKED_NO_THANKS,
    ACTIVATION_MODAL_CLICKED_PRICING,
    ACTIVATION_MODAL_CLICKED_START_TRIAL,
    ACTIVATION_MODAL_OPEN,
    ACTIVATION_MODAL_SOURCE_FIRST_STEP,
    ACTIVATION_MODAL_SOURCE_SECOND_STEP,
    ACTIVATION_MODAL_VIEW_TRIAL_MODAL,
    MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_CLOSE,
    MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_GET_STARTED,
    MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_LEARN_MORE,
    MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_NO_THANKS,
    MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_PRICING,
    MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_START_TRIAL,
    MARKETING_ACTIVATION_TRIAL_MODAL_VIEW,
} from '../constants';

export const trackOnOpen = (amplitudeSource?: string): void => {
    if (amplitudeSource) {
        trackEvent({
            category: ACTIVATION_MODAL_CATEGORY,
            action: ACTIVATION_MODAL_OPEN + amplitudeSource,
            source: amplitudeSource,
        });
    }
    trackEvent({
        category: ACTIVATION_MODAL_CATEGORY,
        action: ACTIVATION_MODAL_VIEW_TRIAL_MODAL,
        source: ACTIVATION_MODAL_SOURCE_FIRST_STEP,
    });
    logEvent(MARKETING_ACTIVATION_TRIAL_MODAL_VIEW);
};

export const trackOnClose = (showSecondStep: boolean): void => {
    trackEvent({
        category: ACTIVATION_MODAL_CATEGORY,
        action: ACTIVATION_MODAL_CLICKED_CLOSE,
        source: showSecondStep
            ? ACTIVATION_MODAL_SOURCE_SECOND_STEP
            : ACTIVATION_MODAL_SOURCE_FIRST_STEP,
    });
    logEvent(MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_CLOSE);
};

const trackLearnMore = (): void => {
    trackEvent({
        category: ACTIVATION_MODAL_CATEGORY,
        action: ACTIVATION_MODAL_CLICKED_LEARN_MORE,
        source: ACTIVATION_MODAL_SOURCE_FIRST_STEP,
    });
    logEvent(MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_LEARN_MORE);
};

const trackPricing = (): void => {
    trackEvent({
        category: ACTIVATION_MODAL_CATEGORY,
        action: ACTIVATION_MODAL_CLICKED_PRICING,
        source: ACTIVATION_MODAL_SOURCE_SECOND_STEP,
    });
    logEvent(MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_PRICING);
};

export const onNoThanksClick = (closeModal: Function): void => {
    trackEvent({
        category: ACTIVATION_MODAL_CATEGORY,
        action: ACTIVATION_MODAL_CLICKED_NO_THANKS,
        source: ACTIVATION_MODAL_SOURCE_FIRST_STEP,
    });
    logEvent(MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_NO_THANKS);
    closeModal();
};

export const reloadOnCloseSecondStep = (showSecondStep: boolean): void => {
    if (showSecondStep) {
        window.location.reload();
    }
};

export const onShowSecondStepClick = (
    enableSecondStep: Function,
    showError: Function,
    trialVersion?: string,
): void => {
    trackEvent({
        category: ACTIVATION_MODAL_CATEGORY,
        action: ACTIVATION_MODAL_CLICKED_START_TRIAL,
        source: ACTIVATION_MODAL_SOURCE_FIRST_STEP,
    });
    logEvent(MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_START_TRIAL);

    const path = `${TD_API_URL}boost/users/me/trials/`;

    const params = {
        trial_version: trialVersion,
    };

    interface ApiError {
        error: boolean;
        message?: string;
        name?: string;
    }

    makeRequest('post', path, params, (error?: ApiError) => {
        if (error) {
            const messageError = error?.message ? error?.message : '';
            showError(messageError);
        } else {
            enableSecondStep();
        }
    });
};

export const onGetStartedClick = (
    getStartedRedirect?: string,
    closeModal?: Function,
): void => {
    trackEvent({
        category: ACTIVATION_MODAL_CATEGORY,
        action: ACTIVATION_MODAL_CLICKED_GET_STARTED,
        source: ACTIVATION_MODAL_SOURCE_SECOND_STEP,
    });
    logEvent(MARKETING_ACTIVATION_TRIAL_MODAL_CLICK_GET_STARTED);
    if (getStartedRedirect) {
        setWindowLocation(getStartedRedirect);
    }
    closeModal?.();
};

export const errorNotification = (errorName: string) => {
    const domainUrl = HAS_WINDOW
        ? window.location.origin
        : 'https://www.eventbrite.com';

    const dictionaryErrorMessages: {
        [key: string]: {
            variant: 'danger' | 'warning' | 'info';
            description: string;
            icon: any;
            cta?: any;
        };
    } = {
        '': {
            variant: 'danger',
            description:
                "We couldn't process your request. Please try again later.",
            icon: <AlertChunky />,
        },
        'Failed to fetch': {
            variant: 'danger',
            description:
                "We couldn't process your request. Please try again later.",
            icon: <AlertChunky />,
        },
        'The organization is in trial period': {
            variant: 'info',
            description: "You're currently in a Boost trial.",
            icon: <InfoChunky />,
        },
        'The organization is in post trial period': {
            variant: 'warning',
            description: 'Your free trial has ended.',
            icon: <FlagChunky />,
            cta: (
                <AlertButton
                    variant="ghost"
                    onClick={() =>
                        setWindowLocation(
                            `${domainUrl}/organizations/marketing/settings/billing/subscribe?returnUrl=true`,
                        )
                    }
                >
                    Subscribe to Boost for full access to our tools
                </AlertButton>
            ),
        },
        'The organization has a non lite active subscription': {
            variant: 'info',
            description:
                "You're already subscribed to Boost and can't start another trial.",
            icon: <InfoChunky />,
        },
        'The user requested does not exist': {
            variant: 'danger',
            description:
                "We couldn't process your request. Please try again later.",
            icon: <AlertChunky />,
        },
    };

    return (
        <div className="eds-align--center-horizontal">
            <div className="boost-activation-notification-position">
                <InlineAlert
                    description={dictionaryErrorMessages[errorName].description}
                    variant={dictionaryErrorMessages[errorName].variant}
                    icon={dictionaryErrorMessages[errorName].icon}
                    cta={dictionaryErrorMessages[errorName].cta}
                />
            </div>
        </div>
    );
};
export interface ButtonsFirstStep {
    enableSecondStep: Function;
    showError: Function;
    closeModal: Function;
}

export interface ButtonsSecondStepProps {
    closeModal: Function;
    getStartedRedirect?: string | undefined;
    secondStepButtonText?: string;
    onGetStartedClick?: () => void;
}

export const SeeAllPlansInformation = () => {
    return (
        <>
            {gettext(
                'You have access to all of the features in Eventbrite Boost Core. Once the trial is over, you can continue your subscription for $50/month.',
            )}
            <div className="text_underline" onClick={() => trackPricing()}>
                <a
                    href="https://www.eventbrite.com/organizations/marketing/settings/billing/subscribe/"
                    target="_blank"
                    rel="noreferrer"
                >
                    {gettext('See all plans')}
                </a>
            </div>
        </>
    );
};
export const LearnMoreInformation = (props: {
    learnMoreInformationText?: string;
}) => {
    return (
        <>
            <div className="email-campaigns-upsell-modal__bottom-bar">
                <p className="eds-text-weight--heavy">
                    {props?.learnMoreInformationText ||
                        gettext(
                            'Curious about the marketing tools you can access?',
                        )}
                </p>
                <Link
                    to={'https://eventbrite.com/boost/'}
                    rel="noopener noreferrer"
                    target="_blank"
                    id="activation-trial-learn-more"
                    onClick={() => trackLearnMore()}
                >
                    {gettext('Learn more about Eventbrite Boost')}
                </Link>
            </div>
        </>
    );
};
export interface ButtonsFirstStep {
    enableSecondStep: Function;
    closeModal: Function;
}

export const ModalSecondStepButtons = (props: ButtonsSecondStepProps) => {
    const handleClick = () => {
        onGetStartedClick(props?.getStartedRedirect, props?.closeModal);
        props?.onGetStartedClick?.();
    };
    return (
        <div className="eds-align--right modal-confirm-get-started-button">
            <Button style="fill" onClick={handleClick} id="get-started-button">
                {props?.secondStepButtonText || gettext('Get started')}
            </Button>
        </div>
    );
};

export const ActivationTrialReadyIcon = (
    <svg
        width="86"
        height="86"
        viewBox="0 0 86 86"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle cx="43" cy="43" r="43" fill="#4BE1A0" />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M58.7222 28.2578L36.5417 50.2816L27.2778 41.0832L23.625 44.7076L36.5417 57.5356L62.375 31.8848L58.7222 28.2578Z"
            fill="white"
        />
    </svg>
);

export const ActivationTrialBackground = () => (
    <svg
        width="533"
        height="530"
        viewBox="0 0 533 530"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M335.445 527.404C308.219 533.74 272.293 529.458 261.754 503.573C251.241 477.742 274.395 445.027 258.963 421.803C247.368 404.355 221.959 404.666 201.282 408.064C144.161 417.452 86.72 435.597 29.5375 426.586C20.6388 425.182 11.1008 422.671 5.62736 415.518C1.63212 410.304 0.510773 403.465 0.153866 396.906C-2.50962 347.926 29.8411 304.383 63.465 268.659C193.626 131.12 364.458 38.9227 550.884 5.59934C572.868 1.72999 595.226 -1.3085 617.471 0.571586C634.563 2.0176 654.552 9.49257 657.69 26.3521C659.437 35.7072 655.239 45.4938 657.381 54.769C659.778 65.1814 669.883 72.4033 680.329 74.6696C690.776 76.9358 701.637 75.2022 712.177 73.4686L804.472 58.2761C832.29 53.6957 865.269 50.7692 884.297 71.5646C901.791 90.6823 898.413 120.67 890.796 145.434C870.119 212.619 827.032 271.338 775.627 319.304C678.622 409.846 551.049 477.231 335.445 527.404Z"
            fill="#3659E3"
            fillOpacity="0.08"
        />
    </svg>
);

const EventMarketingSampleIllustration = () => {
    return (
        <img
            src={
                'https://st.toneden.io/production/images/boost/event-campaign-marketing-sample.png'
            }
        />
    );
};
const EventMetricsSampleIllustration = () => {
    return (
        <img
            src={
                'https://st.toneden.io/production/images/boost/event-metrics-sample.png'
            }
        />
    );
};
const EventTargetingSampleIllustration = () => {
    return (
        <img
            src={
                'https://st.toneden.io/production/images/boost/event-targeting-sample.png'
            }
        />
    );
};

export const TRIAL_FLOW_ITEMS: Array<BenefitsItem> = [
    {
        icon: <Trending color="ui-500" />,
        heading: gettext('Power your growth'),
        info: (
            <p
                className="eds-text-bm"
                dangerouslySetInnerHTML={{
                    __html: gettext(
                        "Take the guesswork out of <span style='font-weight: 700'>email and social media marketing</span> with Eventbrite's intuitive and all-inclusive tools",
                    ).toString(),
                }}
            />
        ),
        illustration: <EventMarketingSampleIllustration />,
    },
    {
        icon: <Stopwatch color="grey-500" />,
        heading: gettext('Less hassle'),
        info: (
            <p
                className="eds-text-bm"
                dangerouslySetInnerHTML={{
                    __html: gettext(
                        'Our tools <span style="font-weight: 700">pre-fill your event\'s details</span> to build, launch and automate campaigns in just a few steps',
                    ).toString(),
                }}
            />
        ),
        illustration: <EventMetricsSampleIllustration />,
    },
    {
        icon: <Religion color="grey-500" />,
        heading: gettext('Data-driven targeting'),
        info: (
            <p
                className="eds-text-bm"
                dangerouslySetInnerHTML={{
                    __html: gettext(
                        'Increase ad performance, email engagement and ticket sales using our <span style="font-weight: 700">proprietary data</span> to target new attendees',
                    ).toString(),
                }}
            />
        ),
        illustration: <EventTargetingSampleIllustration />,
    },
];
